import { EventType } from '../events/event-types.js'

/**
 * The event types pushable on the realtime channel (TO-468, ADR-0049) — the
 * single source of truth for BOTH the notification-worker EventBridge pattern and
 * the frontend `INVALIDATION_MAP` keys (typed `Record<RealtimePushEventType, …>`,
 * so a missing frontend key is a compile error). TO-466 added the offer
 * send-progress events: the four render outcomes and the join's sent/updated.
 */
export const REALTIME_PUSH_EVENT_TYPES = [
	EventType.REALTIME_DIAGNOSTIC_PING,
	EventType.OFFER_LANDING_GENERATED,
	EventType.OFFER_LANDING_FAILED,
	EventType.OFFER_PDF_GENERATED,
	EventType.OFFER_PDF_FAILED,
	EventType.OFFER_SENT,
	EventType.OFFER_UPDATED,
] as const

/** One pushable event type. */
export type RealtimePushEventType = (typeof REALTIME_PUSH_EVENT_TYPES)[number]

/**
 * The `data` fields the publisher forwards per pushable type (TO-466 BR-3); every
 * other field is dropped. The channel carries pointers, never content (ADR-0049
 * BR-9), so the render events keep only their `renderId` and sent/updated only
 * their `renderVersion` — never S3 keys, hashes, render error text or customer
 * identifiers.
 */
export const REALTIME_PUSH_DATA_FIELDS: Record<RealtimePushEventType, readonly string[]> = {
	[EventType.REALTIME_DIAGNOSTIC_PING]: ['message'],
	[EventType.OFFER_LANDING_GENERATED]: ['renderId'],
	[EventType.OFFER_LANDING_FAILED]: ['renderId'],
	[EventType.OFFER_PDF_GENERATED]: ['renderId'],
	[EventType.OFFER_PDF_FAILED]: ['renderId'],
	[EventType.OFFER_SENT]: ['renderVersion'],
	[EventType.OFFER_UPDATED]: ['renderVersion'],
}

/** True when `type` is pushable on the realtime channel. */
export function isRealtimePushEventType(type: string): type is RealtimePushEventType {
	return (REALTIME_PUSH_EVENT_TYPES as readonly string[]).includes(type)
}
